/**
 * SettingsRail — the shared section rail, extracted from ClientSettingsLayout
 * for reuse by the platform shell (S5). The /settings render is unchanged:
 * accent defaults to coral with the exact class strings S4 shipped. The
 * platform shell passes accent="teal" — the glanceable scope difference
 * (coral = client scope, teal-dark = agency scope).
 *
 * A horizontal tab bar of router links (not role=tab). Active items carry a
 * colored bottom rule on the nav's rule, colored text and semibold weight;
 * idle items are ink-2 with a hover state and a transparent rule to keep
 * the footprint stable.
 */

import { type MouseEvent, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { DirtyFormGuardDialog, useDirtyFormGuard } from './DirtyFormGuard';

export interface RailItem {
  id: string;
  label: string;
  path: string;
}

const ACTIVE_CLASSES: Record<'coral' | 'teal', string> = {
  coral: 'border-b-2 border-coral font-semibold text-coral-dark',
  teal: 'border-b-2 border-teal-dark font-semibold text-teal-dark',
};

const IDLE_CLASSES = 'border-b-[2px] border-transparent text-ink-2 hover:text-ink';

export function SettingsRail({
  items,
  accent = 'coral',
  ariaLabel,
}: {
  items: readonly RailItem[];
  accent?: 'coral' | 'teal';
  ariaLabel: string;
}) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const guard = useDirtyFormGuard();
  const [pendingDestination, setPendingDestination] = useState<string | null>(null);

  const handleNavigation = (event: MouseEvent<HTMLAnchorElement>, path: string, active: boolean) => {
    if (
      active ||
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.altKey ||
      event.ctrlKey ||
      event.shiftKey ||
      !guard.isDirty()
    ) {
      return;
    }

    event.preventDefault();
    setPendingDestination(path);
  };

  const finishNavigation = (destination: string) => {
    setPendingDestination(null);
    navigate(destination);
  };

  return (
    <>
      <nav aria-label={ariaLabel} className="border-b border-warm-border">
        <ul className="-mb-px flex gap-1 overflow-x-auto">
          {items.map((s) => {
            const active =
              s.path === '/settings'
                ? pathname === s.path
                : pathname.startsWith(s.path);
            return (
              <li key={s.id} className="shrink-0">
                <Link
                  to={s.path}
                  aria-current={active ? 'page' : undefined}
                  onClick={(event) => handleNavigation(event, s.path, active)}
                  className={cn(
                    'block whitespace-nowrap px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                    active ? ACTIVE_CLASSES[accent] : IDLE_CLASSES,
                  )}
                >
                  {s.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
      {pendingDestination ? (
        <DirtyFormGuardDialog
          onStay={() => setPendingDestination(null)}
          onDiscard={() => {
            guard.discard();
            finishNavigation(pendingDestination);
          }}
          onSave={async () => {
            const result = await guard.save();
            if (typeof result === 'boolean' ? result : result.success) {
              finishNavigation(pendingDestination);
            }
            return result;
          }}
        />
      ) : null}
    </>
  );
}
